import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Seo from "@/components/Seo";
import MarkdownRenderer from "@/components/MarkdownRenderer";
import { ArrowLeft, Tag } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getPost } from "@/content/posts";

export default function BlogPost() {
  const { t, i18n } = useTranslation();
  const { slug: rawSlug } = useParams<{ slug: string }>();
  const dateLocale = i18n.language.startsWith("pt") ? "pt-BR" : "en-US";
  const post = rawSlug ? getPost(rawSlug, i18n.language) : null;

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <Seo title={t("seo.notFound.title")} description={t("seo.notFound.desc")} path="/blog" />
        <Header />
        <main id="main" tabIndex={-1} className="flex-1 py-24 md:py-32 px-6 outline-none">
          <div className="max-w-[1400px] mx-auto">
            <Link to="/blog" className="ed-eyebrow inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors duration-200">
              <ArrowLeft className="h-3.5 w-3.5" /> {t("blogPost.backToBlog")}
            </Link>
            <p className="ed-eyebrow text-muted-foreground mt-12">{t("blogPost.notFoundLabel")}</p>
            <h1 className="ed-display mt-4">
              {t("blogPost.notFoundTitle1")}<br />{t("blogPost.notFoundTitle2")}<span className="text-brand">.</span>
            </h1>
            <p className="ed-serif text-2xl md:text-3xl text-foreground/80 mt-6 max-w-xl">
              {t("blogPost.notFoundSub", { slug: rawSlug })}
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const { frontmatter, content, slug } = post;
  const formattedDate = frontmatter.date
    ? new Date(frontmatter.date).toLocaleDateString(dateLocale, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Seo
        title={frontmatter.title ? `${frontmatter.title} | Avoria` : t("seo.blog.title")}
        description={frontmatter.summary ?? t("seo.blog.desc")}
        path={`/blog/${slug}`}
      />
      <Header />
      <main id="main" tabIndex={-1} className="flex-1 outline-none">
        {/* Back link */}
        <section className="w-full pt-12 md:pt-16 pb-8 px-6">
          <div className="max-w-[1400px] mx-auto">
            <Link to="/blog" className="ed-eyebrow inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors duration-200">
              <ArrowLeft className="h-3.5 w-3.5" /> {t("blogPost.backToBlog")}
            </Link>
          </div>
        </section>

        {/* Editorial article header */}
        <section className="w-full px-6 pb-12 md:pb-16">
          <div className="max-w-[1400px] mx-auto">
            <header className="max-w-5xl">
              {/* Eyebrow meta - date / tags */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 ed-eyebrow text-muted-foreground">
                {formattedDate && (
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand" />
                    {formattedDate}
                  </span>
                )}
                {formattedDate && frontmatter.tags?.length ? (
                  <span aria-hidden="true" className="text-foreground/25">/</span>
                ) : null}
                {frontmatter.tags?.length ? (
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    {frontmatter.tags.map((tag) => (
                      <span key={tag} className="inline-flex items-center gap-1.5">
                        <Tag className="h-3 w-3 opacity-60" aria-hidden="true" />
                        {tag}
                      </span>
                    ))}
                  </span>
                ) : null}
              </div>

              {/* Title */}
              <h1 className="ed-display mt-7">
                {frontmatter.title || slug}
              </h1>

              {/* Serif lede */}
              {frontmatter.summary && (
                <p className="ed-serif text-2xl md:text-4xl text-foreground/80 leading-tight mt-8 max-w-4xl">
                  {frontmatter.summary}
                </p>
              )}
            </header>
          </div>
        </section>

        {/* Hairline */}
        <div className="w-full max-w-[1400px] mx-auto px-6">
          <div className="border-t border-border" />
        </div>

        {/* Article body */}
        <section className="w-full px-6 py-14 md:py-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="grid md:grid-cols-[7rem_minmax(0,46rem)] md:gap-10">
              {/* Serif side annotation */}
              <aside className="hidden md:block">
                <p className="ed-serif text-xl text-muted-foreground sticky top-28">
                  {t("blogPost.theEssay")}
                </p>
              </aside>
              <div>
                <MarkdownRenderer content={content} />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
